import { getFeatureFlag, isValidUrl } from "@sinch/feature-flags";
import { API_BASE_URL } from "./config/legacy-frozen-config";

import {
  getSessionStorage,
  removeSessionStorage,
  setSessionStorage,
} from "./helpers/sessionStorage";

const sessionKey = "project_id";
/**
 * Project id and the token user subject that set it. We should not use the
 * project id if the current subject is different.
 */
type ActiveProjectId = {
  activeProjectId: number | null;
  subject: string | null;
};

function setProjectIdFromURL() {
  // Set the current project id from the hash if available.
  const hash = location.hash.slice(1);
  const params = new URLSearchParams(hash);
  const projectId = parseInt(params.get("_project") ?? "0", 10);
  if (projectId > 0) {
    sessionStorage.setItem("project_id", JSON.stringify(projectId));
  }
}

export const setProjectIdSessionStorage = (
  activeProjectId: number | undefined | null,
  subject: string | null
) =>
  activeProjectId === undefined || activeProjectId === null
    ? removeSessionStorage(sessionKey)
    : setSessionStorage<ActiveProjectId>(sessionKey, {
        activeProjectId,
        subject,
      });

/**
 * Returns the project id from session storage if the subject in null or matches
 * the stored subject.
 */
export const getProjectIdFromSessionStorage = (subject: string | null) => {
  const storedId = getSessionStorage(
    sessionKey,
    (x: unknown): x is ActiveProjectId =>
      !!x && Object.hasOwn(x, "activeProjectId") && Object.hasOwn(x, "subject"),
    null
  );

  return storedId?.subject === subject || subject === null
    ? storedId?.activeProjectId
    : null;
};

export const headerName = "x-project-id";

const urlSymbol = Symbol("url");
const projectIdAddedSymbol = Symbol("headerSet");

declare global {
  interface XMLHttpRequest {
    [urlSymbol]?: string | URL;
    [projectIdAddedSymbol]?: boolean;
  }
}

export const injectProjectIdInRequests = () => {
  const apiHostname = new URL(API_BASE_URL).hostname;

  const shouldSendProjectIdHeader = (url: Request | URL | string) => {
    const hostname = (
      url instanceof URL ? url : new URL(url instanceof Request ? url.url : url)
    ).hostname;

    const apiGatewayUrl = getFeatureFlag(
      "apiGatewayUrl",
      isValidUrl,
      "https://api.appengage.sinch.com"
    );

    const apiGatewayHostname = new URL(apiGatewayUrl).hostname;

    return (
      hostname == apiHostname ||
      hostname.endsWith("." + apiHostname) ||
      hostname == apiGatewayHostname ||
      hostname.endsWith("." + apiGatewayHostname) ||
      hostname.endsWith("localhost")
    );
  };

  //////////////////////////////////////////////////////////////////////////////
  // Patch fetch
  const { fetch: originalFetch } = window;
  window.fetch = (...args) => {
    const patchedRequest = new Request(...args);

    // Add `x-project-id` header to our api requests.
    if (shouldSendProjectIdHeader(args[0])) {
      const projectId = getProjectIdFromSessionStorage(null);
      if (projectId && !patchedRequest.headers.has(headerName)) {
        patchedRequest.headers.set(headerName, JSON.stringify(projectId));
      }
    }

    return originalFetch(patchedRequest);
  };

  //////////////////////////////////////////////////////////////////////////////
  // Patch XMLHttpRequest
  const { setRequestHeader, open, send: xhrSend } = XMLHttpRequest.prototype;
  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (name === headerName) {
      this[projectIdAddedSymbol] = true;
    }
    return setRequestHeader.call(this, name, value);
  };
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, ...args) {
    this[urlSymbol] = args[1];
    // @ts-expect-error this won't type correctly because .open has
    // several overrides.
    open.apply(this, args);
  } as typeof XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.send = function xmlHttpRequestSend(...args) {
    const setProjectIdHeader =
      !this[projectIdAddedSymbol] &&
      shouldSendProjectIdHeader(this[urlSymbol] ?? "");

    if (setProjectIdHeader) {
      const projectId = getProjectIdFromSessionStorage(null);
      if (projectId) {
        this.setRequestHeader(headerName, JSON.stringify(projectId));
      }
    }
    return xhrSend.call(this, ...args);
  };

  // reset to original
  return () => {
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.setRequestHeader = setRequestHeader;
  };
};

/**
 * Enables the projectId header in outgoing requests, and reads it from #_project in the URL.
 */
export const enableProjectIdHeader = () => {
  setProjectIdFromURL();
  injectProjectIdInRequests();
};
